class RecentlyPurchasedPopup extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    // Don't initialize if on mobile
    if (window.innerWidth <= 768) {
      this.remove();
      return;
    }

    if (this.isConnected) {
      this.cacheElements();
    }
  
    if (this.isConnected && !this.initialized) {
      this.initialized = true;
      this.attachListeners();
      this.startShowing();
    }
  }
  
  disconnectedCallback() {
    if (!this.isConnected && this.initialized) {
      this.initialized = false;
      this.detachListeners();
    }
  }
  
  cacheElements() {
    try {
      this.sectionInfoJSON = JSON.parse(
        this.querySelector('[data-recently-purchased-popup-json]').innerHTML
      );
      this.settings = this.sectionInfoJSON.settings;
      this.items = this.sectionInfoJSON.items;

      // Randomize order of items shown
      this.shuffleItems();
    } catch(err) {
      console.warn('Recently purchased popup settings not property formatted.');
      this.remove();
      return;
    }

    this.closePopup = this.closePopup.bind(this)
    this.innerContents = this.querySelector('.RecentlyPopup__Inner');
    this.closeBtn = this.querySelector('.RecentlyPopup__Close');

    this.notificationsShown = 0;
    this.notificationIndex = 0;
  }

  async startShowing() {
    // A flag that can be updated to stop showing notifications
    this.shouldStop = false;

    // First handle initial delay
    await this.showAfterSeconds(this.settings.initial_delay);
    this.notificationsShown += 1;

    // Then handle the rest
    while(this.notificationsShown < this.settings.max_notifications) {
      if (this.shouldStop) {
        console.debug('Stopping recently purchased popup early.');
        return;
      }

      let delay = this.settings.delay_between_notifications;
      if (this.settings.randomize_delay) {
        delay += (Math.random() * 60) - 30;

        // Minimum delay must be more seconds than the display time
        delay = Math.max(delay, this.settings.display_time + 2);
      }

      await this.showAfterSeconds(delay);
      this.notificationsShown += 1;
    }
  }

  showAfterSeconds(seconds) {
    return new Promise((resolve) => {
      setTimeout(async () => {
        await this.openPopupAndCloseAfterDelay();
        resolve();
      }, (seconds * 1000));
    });
  }

  /**
   * Note: Promise doesn't wait for the popup to close, just for when it opens.
   */
  async openPopupAndCloseAfterDelay() {
    try {
      await this.populateContents();
    } catch(err) {
      console.warn('populateContents', err);
      this.shouldStop = true;
      return;
    }

    this.openPopup();

    // Close popup after a delay
    const ms = this.settings.display_time * 1000;
    setTimeout(() => {
      this.closePopup();
    }, ms);
  }

  /**
   * Grab the next item in the list of slides.
   */
  async populateContents() {
    const item = this.items[this.notificationIndex];
    this.notificationIndex += 1;
    if (this.notificationIndex >= this.items.length) {
      this.notificationIndex = 0;
    }

    const html = await this.getHTMLForItem(item);
    this.innerContents.innerHTML = html;
  }

  async getHTMLForItem(item) {
    const product = await fetch(`/products/${item.product}.js`)
      .then(res => res.json());
    
    let image;
    if (product.featured_image) {
      image = product.featured_image;

      // Request the thumbnail version of the image
      const extIndex = product.featured_image.search(/\.(png|jpg)\?/i);
      if (extIndex > -1) {
        image = product.featured_image.slice(0, extIndex)
          + '_small'
          + product.featured_image.slice(extIndex);
      }
    }
    
    return (`
      ${image
        ? `<img class="RecentlyPopup__Image" src="${image}" alt="${product.title}"></img>`
        : ''
      }
      <div class="RecentlyPopup__Content">
        ${item.heading}<br>
        <a href="${product.url}">${product.title}</a><br>
        <span>${item.subtext}</span>
      </div>
    `);
  }

  openPopup() {
    this.setAttribute('aria-hidden', 'false');
  }

  closePopup() {
    this.setAttribute('aria-hidden', 'true');
  }

  attachListeners() {
    this.closeBtn.addEventListener('click', this.closePopup);
  }

  detachListeners() {
    this.closeBtn.removeEventListener('click', this.closePopup);
  }

  shuffleItems() {
    let currentIndex = this.items.length;
    let randomIndex;
  
    while (currentIndex != 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
  
      [this.items[currentIndex], this.items[randomIndex]] =
        [this.items[randomIndex], this.items[currentIndex]];
    }
  }
}
customElements.define('recently-purchased-popup', RecentlyPurchasedPopup);
